import React, { useMemo, useState } from 'react';
import { Cloud, GitCompare } from 'lucide-react';
import toolkitService from '../services/ToolkitService';
import Button from './shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import ToolkitFileStep, { STEP, stepIcon } from './toolkit/ToolkitFileStep';
import ToolkitCompareResults from './toolkit/ToolkitCompareResults';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › Cancellation Check — ported from the desktop's Balancing › Cancellation Check. Every
// reservation CANCELLED in a v5 Res Detail report is looked up in Opera Cloud by its v5 confirmation number (the
// external reference) among the reservations departing in a date range: Match (cancelled there too), Mismatch (still
// live in Opera Cloud) or Not found.
//
// The Opera Cloud side is fetched a week at a time (toolkit property.js list-departures), so a long range never meets
// the API timeout, and with EVERY status — the desktop asked for all statuses but fetched Reserved only, so a
// reservation correctly cancelled in Opera Cloud was never found.

const CANCELLED_V5 = ['CXL', 'CANCELLED', 'Cancelled', 'Cancel'];
const cancelledInCloud = (s) => ['cancelled', 'cxl', 'cancel'].includes(String(s || '').toLowerCase());

// Whole UTC days, so a daylight-saving change inside the range cannot shift a chunk by a day.
const weeks = (from, to) => {
    const out = [];
    const end = new Date(to);
    for (let d = new Date(from); d <= end;) {
        const e = new Date(d);
        e.setUTCDate(e.getUTCDate() + 6);
        const chunkEnd = e > end ? end : e;
        out.push({ start: d.toISOString().slice(0, 10), end: chunkEnd.toISOString().slice(0, 10) });
        d = new Date(chunkEnd);
        d.setUTCDate(d.getUTCDate() + 1);
    }
    return out;
};

const COLUMNS = [
    { key: 'v5ConfNumber', label: 'v5 conf' },
    { key: 'v5GuestName', label: 'Guest (v5)' },
    { key: 'v5ArrivalDate', label: 'Arrival v5 / Cloud', render: (r) => `${r.v5ArrivalDate || '—'} / ${r.cloudArrivalDate || '—'}` },
    { key: 'v5DepartureDate', label: 'Departure v5 / Cloud', render: (r) => `${r.v5DepartureDate || '—'} / ${r.cloudDepartureDate || '—'}` },
    { key: 'cloudConfNumber', label: 'Cloud reservation' },
    { key: 'cloudStatus', label: 'Cloud status' },
];
const STATUSES = [{ value: 'match', label: 'Cancelled in both' }, { value: 'mismatch', label: 'Still live in Cloud' }, { value: 'not-found', label: 'Not found in Cloud' }];

function ToolkitCancellationCheckPage() {
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;
    const [dateFormat, setDateFormat] = useState('');
    const [v5Cxl, setV5Cxl] = useState(null);
    const [v5Count, setV5Count] = useState(0);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [cloud, setCloud] = useState(null);
    const [pull, setPull] = useState({ state: STEP.IDLE, message: '' });
    const [results, setResults] = useState(null);

    const onV5 = (rows) => {
        setV5Count(rows.length);
        setV5Cxl(rows.filter((r) => CANCELLED_V5.includes(String(r.RESERVATION_STATUS || '').trim())));
        setResults(null);
        // Default the range to the cancelled reservations' departures.
        const deps = rows.map((r) => r.DEPARTURE_DATE).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)).sort();
        if (deps.length) { setFrom(deps[0]); setTo(deps[deps.length - 1]); }
    };

    const fetchCloud = async () => {
        const chunks = weeks(from, to);
        const all = [];
        setResults(null);
        try {
            for (let i = 0; i < chunks.length; i++) {
                setPull({ state: STEP.BUSY, message: `Fetching week ${i + 1} of ${chunks.length} (${chunks[i].start} → ${chunks[i].end})… ${all.length} so far` });
                const { data } = await toolkitService.property(ctx, 'list-departures', chunks[i]);
                all.push(...data.result.items);
            }
            setCloud(all);
            setPull({ state: STEP.DONE, message: `${all.length} Opera Cloud reservation(s) departing ${from} → ${to}.` });
        } catch (err) {
            setPull({ state: STEP.ERROR, message: err?.response?.data?.message || err?.response?.data?.error?.message || err.message });
        }
    };

    const doCompare = () => {
        const byConf = new Map(cloud.filter((c) => c.v5ConfNumber).map((c) => [c.v5ConfNumber, c]));
        setResults(v5Cxl.map((v) => {
            const conf = String(v.CONFIRMATION_NUMBER || '').trim();
            const c = byConf.get(conf);
            const status = !c ? 'not-found' : cancelledInCloud(c.reservationStatus) ? 'match' : 'mismatch';
            return {
                status, statusText: STATUSES.find((s) => s.value === status).label,
                v5ConfNumber: conf, v5GuestName: v.GUEST_NAME || '', v5ArrivalDate: v.ARRIVAL_DATE || '', v5DepartureDate: v.DEPARTURE_DATE || '',
                cloudConfNumber: c?.resvNameId || '', cloudArrivalDate: c?.arrivalDate || '', cloudDepartureDate: c?.departureDate || '',
                cloudStatus: c?.reservationStatus || '',
            };
        }));
    };

    const summary = useMemo(() => {
        if (!results) return [];
        const n = (s) => results.filter((r) => r.status === s).length;
        return [
            { label: 'v5 reservations', value: v5Count }, { label: 'Cancelled in v5', value: results.length },
            { label: 'Cancelled in both', value: n('match'), tone: 'ok' },
            { label: 'Still live in Cloud', value: n('mismatch'), tone: 'fail' },
            { label: 'Not found in Cloud', value: n('not-found'), tone: 'warn' },
        ];
    }, [results, v5Count]);

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="Cancellation Check"
                subtitle="Check that every reservation cancelled in Opera v5 is cancelled in Opera Cloud too." />

            <ToolkitFileStep number={1} title="Opera v5 Res Detail report" label="Choose the v5 report (XML or CSV)"
                accept=".xml,.rep,.csv,.txt" kind="reservations" parseOptions={{ hotelDateFormat: dateFormat || null }}
                onParsed={onV5} disabled={!ready}>
                <label className="tkb-inline">
                    <span>Ambiguous dates (04.05.26)</span>
                    <select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)}>
                        <option value="">Decide from the file (else month first)</option>
                        <option value="dd-MM-yy">Day first</option>
                        <option value="MM-dd-yy">Month first</option>
                    </select>
                </label>
                {v5Cxl && <p className="tkb-msg">{v5Cxl.length} cancelled of {v5Count} reservation(s).</p>}
            </ToolkitFileStep>

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">2</span> Opera Cloud reservations departing {stepIcon(pull.state)}</div>
                <div className="tkb-row">
                    <label><span>From</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
                    <label><span>To</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
                    <Button variant="secondary" onClick={fetchCloud} disabled={!ready || !from || !to || from > to || pull.state === STEP.BUSY}>
                        <Cloud className="icon" /> Fetch
                    </Button>
                    <Button variant="success" onClick={doCompare} disabled={!v5Cxl || !cloud || pull.state === STEP.BUSY}>
                        <GitCompare className="icon" /> Compare
                    </Button>
                </div>
                {pull.message && <p className={`tkb-msg${pull.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{pull.message}</p>}
            </div>

            {results && (
                <ToolkitCompareResults summary={summary} rows={results} columns={COLUMNS} statuses={STATUSES} exportName="Cancellation_Check" />
            )}
        </div>
    );
}

export default ToolkitCancellationCheckPage;
